<template>
  <component :is="useCard">
    <div class="map-layout">
      <div class="map-scroll">
        <div class="map" :style="{ width: `${W}px`, height: `${H}px` }">
          <svg class="edges" :width="W" :height="H">
            <defs>
              <marker v-for="(color, key) in colors" :key="key" :id="`eco978-arr-${key}`"
                      viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                <path d="M 0 0 L 10 5 L 0 10 z" :fill="color"/>
              </marker>
            </defs>
            <path v-for="(edge, index) in edges" :key="index"
                  :d="edge.d" fill="none"
                  :stroke="colors[edge.c]" :stroke-width="edge.w || 1.5"
                  :stroke-dasharray="edge.dash"
                  :marker-end="edge.arrow ? `url(#eco978-arr-${edge.c})` : null"
                  :class="{ dim: edgeDimmed(edge) }"/>
          </svg>

          <div v-for="header in headers" :key="header.text" class="col-header" :style="rect(header)">
            &lt;{{ header.text }}&gt;
          </div>

          <template v-for="group in groups">
            <div v-if="!group.children" :key="group.code" class="node single"
                 :class="{ dim: nodeDimmed(group.code) }"
                 :style="rect(group, 44)" @click="onClickNode(group.code)">
              <span>{{ group.label }}<br>[{{ group.code }}]</span>
            </div>
            <div v-else :key="group.code" class="group"
                 :class="{ dim: groupDimmed(group) }"
                 :style="rect(group, groupHeight(group))">
              <div class="group-header" @click="onClickNode(group.code)">
                {{ group.label }}<br>[{{ group.code }}]
              </div>
              <div v-for="child in group.children" :key="child.code" class="node child"
                   :style="{ height: `${child.h || 38}px`, marginTop: `${child.mt || 6}px` }" @click="onClickNode(child.code)">
                <span>{{ child.label }}<br>[{{ child.code }}]</span>
              </div>
            </div>
          </template>

          <!-- 생산품 -->
          <div v-for="(table, index) in products" :key="`table-${index}`" class="product-table"
               :class="{ dim: hoverKey && hoverKey !== 'product' }" :style="rect(table)">
            <div v-for="cell in table.cells" :key="cell.code" class="cell" @click="onClickNode(cell.code)">
              {{ cell.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- 범례: 도면 우측에서 스크롤을 따라다님 -->
      <div class="legend-wrap">
        <div class="legend">
          <h1>※ 범 례</h1>
          <div class="legend-grid">
            <div v-for="item in legendItems" :key="item.key" class="legend-row cursor-pointer"
                 :class="{ dim: hoverKey && hoverKey !== item.key }"
                 @mouseenter="hoverKey = item.key" @mouseleave="hoverKey = ''">
              <div class="name">{{ item.name }}</div>
              <div class="line">
                <svg width="90" height="10">
                  <path d="M 0 5 H 82" fill="none" :stroke="colors[item.c]" stroke-width="1.5"
                        :stroke-dasharray="item.dash" :marker-end="`url(#eco978-arr-${item.c})`"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
export default {
  props: {
    card: {
      type: Boolean,
      default: true
    },
  },
  computed: {
    useCard() {
      if (this.card) {
        return 'vx-card'
      }

      return 'div'
    }
  },
  data() {
    return {
      W: 1150,
      H: 1220,
      colors: {
        black: '#000000',
        orange: '#ED7D31',
        blue: '#5B9BD5',
        green: '#70AD47',
      },
      headers: [
        { text: '유틸리티 공정', x: 40, y: 20, w: 210, h: 30 },
        { text: '제품제조공정', x: 390, y: 20, w: 220, h: 30 },
        { text: '환경오염물질처리공정', x: 870, y: 20, w: 230, h: 30 },
        { text: '기타공정', x: 870, y: 625, w: 230, h: 30 },
      ],
      groups: [
        // 유틸리티 공정
        { code: 'PU-01', label: '주원료처리공정', x: 40, y: 60, w: 210 },
        { code: 'PU-02', label: '부원료처리공정', x: 40, y: 124, w: 210, children: [
          { code: 'PU-02-01', label: '부원료보관장 입고공정' },
          { code: 'PU-02-02', label: '전기로부원료 처리공정' },
          { code: 'PU-02-03', label: '정련로부원료처리공정' },
        ]},
        { code: 'PU-03', label: '용수정수공정', x: 40, y: 318, w: 210 },
        { code: 'PU-04', label: '간접냉각수처리공정', x: 40, y: 382, w: 210, children: [
          { code: 'PU-04-01', label: '형강제강간접수처리공정' },
          { code: 'PU-04-02', label: '형강 연주·압연 간접수처리공정', h: 50 },
          { code: 'PU-04-03', label: '봉강압연간접수처리공정' },
        ]},
        { code: 'PU-05', label: '사용물질저장공정', x: 40, y: 588, w: 210, children: [
          { code: 'PU-05-01', label: '형강폐수처리약품공정' },
          { code: 'PU-05-02', label: '봉강폐수처리약품공정' },
          { code: 'PU-05-03', label: '이화학시험공정' },
          { code: 'PU-05-04', label: '형강암모니아공급공정' },
          { code: 'PU-05-05', label: '봉강암모니아공급공정' },
        ]},
        { code: 'PU-06', label: '기타용수사용공정', x: 40, y: 870, w: 210, children: [
          { code: 'PU-06-01', label: '세륜세척공정' },
          { code: 'PU-06-02', label: '이화학시험 및 기타공정' },
        ]},
        { code: 'PU-07', label: '공기압축공정', x: 40, y: 1020, w: 210 },

        // 제품제조공정
        { code: 'P-01', label: '제강연주공정', x: 390, y: 60, w: 220, children: [
          { code: 'P-01-01', label: '전기로공정' },
          { code: 'P-01-02', label: '정련로공정' },
          { code: 'P-01-03', label: '연주공정' },
        ]},
        { code: 'P-02', label: '형강제조공정', x: 390, y: 280, w: 220, children: [
          { code: 'P-02-01', label: '가열로공정' },
          { code: 'P-02-02', label: '압연공정' },
          { code: 'P-02-03', label: '정정공정' },
        ]},
        { code: 'P-03', label: '봉강제조공정', x: 390, y: 500, w: 220, children: [
          { code: 'P-03-01', label: '가열로공정' },
          { code: 'P-03-02', label: '압연공정' },
          { code: 'P-03-03', label: '원철근공정' },
          { code: 'P-03-04', label: '코일철근공정', mt: 20 },
        ]},

        // 환경오염물질처리공정
        { code: 'PW-01', label: '형강폐수처리공정', x: 870, y: 60, w: 230, children: [
          { code: 'PW-01-01', label: '연주직접수처리공정' },
          { code: 'PW-01-02', label: '형강 연주·압연 직접수처리공정', h: 50 },
          { code: 'PW-01-03', label: '형강폐수처리공정' },
        ]},
        { code: 'PW-02', label: '봉강폐수처리공정', x: 870, y: 266, w: 230, children: [
          { code: 'PW-02-01', label: '봉강압연직접수처리공정' },
          { code: 'PW-02-02', label: '봉강폐수처리공정' },
        ]},
        { code: 'PW-03', label: '폐기물보관 및 처리공정', x: 870, y: 416, w: 230, children: [
          { code: 'PW-03-01', label: '슬러그처리공정' },
          { code: 'PW-03-02', label: '분진외폐기물보관공정' },
        ]},
        { code: 'PW-04', label: '비점오염원처리공정', x: 870, y: 566, w: 230 },

        // 기타공정
        { code: 'PE-01', label: '지원설비공정', x: 870, y: 660, w: 230, children: [
          { code: 'PE-01-01', label: '전기로 스케일제거공정' },
          { code: 'PE-01-02', label: '스카핑공정' },
          { code: 'PE-01-03', label: 'Roll 정비공정' },
          { code: 'PE-01-04', label: '건닝재 투입공정' },
        ]},
        { code: 'PE-02', label: '위험물저장공정', x: 870, y: 898, w: 230 },
        { code: 'PE-03', label: '건조·예열공정', x: 870, y: 962, w: 230, children: [
          { code: 'PE-03-01', label: 'Ladle 건조공정' },
          { code: 'PE-03-02', label: 'Tundish 건조공정' },
          { code: 'PE-03-03', label: 'Ladle 승열공정' },
          { code: 'PE-03-04', label: 'Tundish 예열공정' },
        ]},
      ],
      products: [
        { x: 680, y: 450, w: 140, h: 48, cells: [
          { label: '생산품', code: 'P-02_생산품' },
          { label: '처리', code: 'P-02_처리' },
          { label: '형강류', code: 'P-02_형강류' },
          { label: '판매', code: 'P-02_판매' },
        ]},
        { x: 680, y: 652, w: 140, h: 48, cells: [
          { label: '생산품', code: 'P-03-03_생산품' },
          { label: '처리', code: 'P-03-03_처리' },
          { label: '원철근', code: 'P-03-03_원철근' },
          { label: '판매', code: 'P-03-03_판매' },
        ]},
        { x: 680, y: 715, w: 140, h: 48, cells: [
          { label: '생산품', code: 'P-03-04_생산품' },
          { label: '처리', code: 'P-03-04_처리' },
          { label: '코일철근', code: 'P-03-04_코일철근' },
          { label: '판매', code: 'P-03-04_판매' },
        ]},
      ],
      legendItems: [
        { key: 'raw', name: '원료,부원료,약품', c: 'black' },
        { key: 'product', name: '생산품(완제품, 반제품)', c: 'orange' },
        { key: 'water', name: '용수', c: 'blue' },
        { key: 'waste', name: '환경오염물질(폐수, 폐기물)', c: 'black', dash: '6 4' },
        { key: 'air', name: 'AIR', c: 'green', dash: '10 4 2 4' },
      ],
      // 범례 hover 시 강조할 관련 공정 (카테고리 → 공정코드)
      related: {
        raw: ['PU-01', 'PU-02-01', 'P-01', 'P-03-02', 'P-03-04', 'PU-05-01', 'PU-05-02', 'PW-01-02', 'PW-02-01'],
        product: ['P-01', 'P-01-03', 'P-02', 'P-02-03', 'P-03-02', 'P-03-03', 'P-03-04'],
        water: ['PU-03', 'PU-04-03', 'P-01-01', 'P-01-03', 'P-02', 'P-03-01'],
        waste: ['P-01', 'P-02', 'P-03', 'PW-01', 'PW-02-01', 'PW-03', 'PW-03-01'],
        air: ['PU-07', 'P-01-02', 'P-02-02', 'P-03-01'],
      },
      hoverKey: '',
      edges: [
        // 원료, 부원료, 약품 (흑색 실선)
        // 주원료처리공정 → 제강연주공정, 부원료보관장 입고공정(PU-02-01)이 합류
        { k: 'raw', c: 'black', d: 'M 250 82 H 386', w: 2, arrow: true },
        { k: 'raw', c: 'black', d: 'M 250 185 H 290 V 82', w: 2 },
        // 형강/봉강폐수처리약품공정 → 지원설비공정·위험물저장공정 사이 틈으로 가로질러 우측 폐수처리공정으로 공급
        { k: 'raw', c: 'black', d: 'M 250 649 H 290', w: 2 },
        { k: 'raw', c: 'black', d: 'M 250 693 H 290', w: 2 },
        { k: 'raw', c: 'black', d: 'M 290 649 V 888 H 344 A 6 6 0 0 1 356 888 H 1130 V 171 H 1104', w: 2, arrow: true },
        { k: 'raw', c: 'black', d: 'M 1130 327 H 1104', w: 2, arrow: true },

        // 용수 (청색 실선)
        // 용수정수공정(PU-03) → 연주공정 직결 : 다른 파란 트렁크·초록 세로선은 넓은 점프로 통과 (합류 아님)
        { k: 'water', c: 'blue', d: 'M 250 340 H 310 V 209 H 324 A 16 16 0 0 1 356 209 H 386', arrow: true },
        // 봉강압연간접수처리공정(PU-04-03) → 전기로/형강제조/봉강가열로
        { k: 'water', c: 'blue', d: 'M 250 543 H 330' },
        { k: 'water', c: 'blue', d: 'M 330 121 V 569' },
        { k: 'water', c: 'blue', d: 'M 330 121 H 386', arrow: true },
        // 초록(AIR) 세로선과 교차하는 지점은 점프(호)로 구분
        { k: 'water', c: 'blue', d: 'M 330 301 H 344 A 6 6 0 0 1 356 301 H 386', arrow: true },
        { k: 'water', c: 'blue', d: 'M 330 569 H 344 A 6 6 0 0 1 356 569 H 386', arrow: true },

        // AIR (녹색 일점쇄선) : 공기압축공정 → 정련로/형강압연/봉강가열로
        { k: 'air', c: 'green', d: 'M 250 1042 H 350', dash: '10 4 2 4' },
        { k: 'air', c: 'green', d: 'M 350 165 V 1042', dash: '10 4 2 4' },
        { k: 'air', c: 'green', d: 'M 350 165 H 386', dash: '10 4 2 4', arrow: true },
        { k: 'air', c: 'green', d: 'M 350 385 H 386', dash: '10 4 2 4', arrow: true },
        { k: 'air', c: 'green', d: 'M 350 553 H 386', dash: '10 4 2 4', arrow: true },

        // 생산품 (주황색 실선)
        { k: 'product', c: 'orange', d: 'M 500 234 V 276', arrow: true },
        { k: 'product', c: 'orange', d: 'M 610 147 H 640 V 610' },
        { k: 'product', c: 'orange', d: 'M 640 610 H 614', arrow: true },
        // 정정공정 하단 → 형강류 생산품 표 (주황 세로 트렁크는 점프로 통과)
        { k: 'product', c: 'orange', d: 'M 500 454 V 476 H 634 A 6 6 0 0 1 646 476 H 676', arrow: true },
        // 원철근/코일철근공정 하단에서 바로 아래로 빠져나와 생산품 표로 직결
        { k: 'product', c: 'orange', d: 'M 500 668 V 678 H 676', arrow: true },
        { k: 'product', c: 'orange', d: 'M 500 726 V 741 H 676', arrow: true },
        // 압연공정 → 코일철근공정 (그룹 테두리를 뚫고 박스끼리 직접 연결된 형태, 원료 범례에서만 강조)
        { k: 'raw', c: 'black', d: 'M 396 605 H 378 V 707 H 410', arrow: true },

        // 환경오염물질 (흑색 점선)
        // A계통: 제강연주(P-01)·형강제조(P-02)가 좌측 세로선(x=655)에서 합류
        //        → 중간 연결선(y=190) → 우측 세로선(x=850) → 형강폐수처리(PW-01) + 폐기물보관및처리(PW-03)
        { k: 'waste', c: 'black', d: 'M 610 80 H 655', dash: '6 4' },
        { k: 'waste', c: 'black', d: 'M 850 80 H 866', dash: '6 4', arrow: true },
        { k: 'waste', c: 'black', d: 'M 610 301 H 634 A 6 6 0 0 1 646 301 H 655', dash: '6 4' },
        { k: 'waste', c: 'black', d: 'M 655 80 V 301', dash: '6 4' },
        { k: 'waste', c: 'black', d: 'M 655 190 H 850', dash: '6 4' },
        { k: 'waste', c: 'black', d: 'M 850 80 V 437', dash: '6 4' },
        { k: 'waste', c: 'black', d: 'M 850 437 H 866', dash: '6 4', arrow: true },
        // B계통: 봉강제조(P-03) → 봉강폐수처리(PW-02) + 폐기물보관및처리(PW-03)
        //        PW-02 진입선은 A계통 우측 세로선(x=850)을 점프로 통과
        { k: 'waste', c: 'black', d: 'M 610 588 H 634 A 6 6 0 0 1 646 588 H 830', dash: '6 4' },
        { k: 'waste', c: 'black', d: 'M 830 327 V 588', dash: '6 4' },
        { k: 'waste', c: 'black', d: 'M 830 327 H 844 A 6 6 0 0 1 856 327 H 866', dash: '6 4', arrow: true },
        { k: 'waste', c: 'black', d: 'M 830 477 H 866', dash: '6 4', arrow: true },
      ],
    }
  },
  methods: {
    isRelated(code) {
      const codes = this.related[this.hoverKey]
      return codes ? codes.includes(code) : true
    },
    edgeDimmed(edge) {
      if (!this.hoverKey) {
        return false
      }

      const keys = Array.isArray(edge.k) ? edge.k : [edge.k]
      return !keys.includes(this.hoverKey)
    },
    nodeDimmed(code) {
      return !!this.hoverKey && !this.isRelated(code)
    },
    groupDimmed(group) {
      return !!this.hoverKey && !this.isRelated(group.code)
        && !group.children.some(child => this.isRelated(child.code))
    },
    groupHeight(group) {
      // 안쪽 여백(6) + 헤더(30) + 하위공정(기본 38) + 하위공정 간격(기본 6, mt로 개별 조정)
      const childrenHeight = group.children.reduce((sum, child) => sum + (child.h || 38) + (child.mt || 6), 0)
      return 6 + 30 + childrenHeight + 6
    },
    rect(o, defaultHeight) {
      return {
        left: `${o.x}px`,
        top: `${o.y}px`,
        width: `${o.w}px`,
        height: `${o.h || defaultHeight}px`,
      }
    },
    onClickNode(itemId) {
      this.$emit('node', itemId)
    }
  }
}
</script>

<style lang="scss" scoped>
.map-layout {
  display: flex;
  justify-content: center;
  gap: 24px;
  padding: 2rem 1rem 4rem;
}
.map-scroll {
  min-width: 0;
  overflow-x: auto;
}
.map {
  position: relative;
}
.edges {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1; // 노드 위로 선을 그려서, 그룹 박스를 관통하는 연결도 보이게 함
  pointer-events: none;

  path { transition: opacity 0.15s }
  path.dim { opacity: 0.08 }
}
.node, .group, .product-table { transition: opacity 0.15s }
.node.dim, .group.dim, .product-table.dim { opacity: 0.2 }
.col-header {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 1.05rem;
  font-weight: 700;
}
.node {
  display: flex;
  border: 1px solid #333333;
  background-color: #ffffff;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: 13px;
  line-height: 1.25;
  cursor: pointer;

  &.single { position: absolute }
  &.child { margin-top: 6px }

  &:hover { background-color: #E5E5E5; border: 1px solid #000000 !important; }
}
.group {
  position: absolute;
  border: 1px solid #333333;
  background-color: #ffffff;
  padding: 6px;

  .group-header {
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
    cursor: pointer;

    &:hover { background-color: #E5E5E5 }
  }
}
.product-table {
  position: absolute;
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid #333333;
  border-left: 1px solid #333333;

  .cell {
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #333333;
    border-bottom: 1px solid #333333;
    background-color: #ffffff;
    font-size: 13px;
    cursor: pointer;

    &:hover { background-color: #E5E5E5 }
  }
}
.legend-wrap {
  flex: 0 0 300px;
}
.legend {
  position: sticky;
  top: 100px; // 상단 고정 헤더 높이만큼 띄움
  border: 1px solid #333333;
  background-color: #ffffff;
  padding: 8px 12px;

  h1 {
    font-size: 0.95rem;
    font-weight: 700;
    margin-bottom: 6px;
  }
  .legend-grid {
    display: grid;
    row-gap: 2px;
  }
  .legend-row {
    display: grid;
    grid-template-columns: 1fr 96px;
    align-items: center;
    padding: 4px 6px;
    border-radius: 4px;
    transition: opacity 0.15s, background-color 0.15s;

    &:hover { background-color: #F3F4F6 }
    &.dim { opacity: 0.35 }

    .name { font-size: 12px }
    .line { display: flex; align-items: center; justify-content: flex-end }
  }
}
</style>
